import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { userContactService } from '@/lib/api/user-contact.service';
import { useToast } from '@/hooks/use-toast';
import { ImportContactsResponse } from '@/types/user-contact.types';

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  celebrationId?: number | null;
}

type ContactMapping = {
  full_name: string;
  email: string;
  phone: string;
  relationship: string;
};

const EMPTY_MAPPING: ContactMapping = {
  full_name: '',
  email: '',
  phone: '',
  relationship: ''
};

const parseCsvHeaders = (text: string): string[] => {
  const firstLine = text.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? '';
  const headers: string[] = [];
  let header = '';
  let quoted = false;

  for (let index = 0; index < firstLine.length; index += 1) {
    const character = firstLine[index];
    if (character === '"') {
      if (quoted && firstLine[index + 1] === '"') {
        header += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      headers.push(header.trim());
      header = '';
    } else {
      header += character;
    }
  }

  headers.push(header.trim());
  return headers.filter((header, index, values) => header.length > 0 && values.indexOf(header) === index);
};

const getAutoMapping = (headers: string[]): ContactMapping => {
  const mapping = { ...EMPTY_MAPPING };
  for (const header of headers) {
    const normalizedHeader = header.toLowerCase();
    if (!mapping.full_name && (normalizedHeader.includes('full_name') || normalizedHeader.includes('fullname') || normalizedHeader === 'name' || normalizedHeader.includes('full name'))) {
      mapping.full_name = header;
    } else if (!mapping.email && (normalizedHeader.includes('email') || normalizedHeader.includes('e-mail') || normalizedHeader.includes('mail'))) {
      mapping.email = header;
    } else if (!mapping.phone && (normalizedHeader.includes('phone') || normalizedHeader.includes('tel') || normalizedHeader.includes('mobile') || normalizedHeader.includes('cell'))) {
      mapping.phone = header;
    } else if (!mapping.relationship && (normalizedHeader.includes('relationship') || normalizedHeader.includes('relation') || normalizedHeader.includes('type') || normalizedHeader.includes('category'))) {
      mapping.relationship = header;
    }
  }
  return mapping;
};

export const ImportContactsModal: React.FC<ImportContactsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  celebrationId
}) => {
  const { t } = useTranslation('contacts');
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ContactMapping>(EMPTY_MAPPING);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportContactsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const resetImportState = () => {
    setFile(null);
    setHeaders([]);
    setMapping(EMPTY_MAPPING);
    setResult(null);
    setError(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    resetImportState();

    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const detectedHeaders = parseCsvHeaders(text);
        if (detectedHeaders.length === 0) {
          setFile(null);
          setError('No CSV headers were found.');
          return;
        }

        setHeaders(detectedHeaders);
        setMapping(getAutoMapping(detectedHeaders));
      };
      reader.onerror = () => {
        setFile(null);
        setError('Unable to read the selected CSV file.');
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleMappingChange = (field: keyof typeof mapping, value: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setError(t('import.errors.noFile'));
      return;
    }

    if (!mapping.full_name) {
      setError(t('import.errors.nameRequired'));
      return;
    }

    if (!mapping.email && !mapping.phone) {
      setError('Map either an email or phone column.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await userContactService.importContacts(file, mapping, celebrationId ?? undefined);
      setResult(response);
      
      if (response.success) {
        const addedCount = response.imported ?? 0;
        const skippedCount = response.skipped ?? 0;
        const skippedText = skippedCount > 0
          ? `, skipped ${skippedCount} contact${skippedCount > 1 ? 's' : ''}`
          : '';

        toast({
          title: 'Contacts imported',
          description: `Added ${addedCount} contact${addedCount > 1 ? 's' : ''}${skippedText}.`
        });

        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('import.errors.importFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetImportState();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="import-modal">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>{t('import.title')}</CardTitle>
              <CardDescription>
                {t('import.subtitle')}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose} aria-label="Close import contacts dialog">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {result ? (
            <div className="space-y-4">
              {result.success ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">{t('import.success')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">{t('import.failed')}</span>
                </div>
              )}
              
              <div className="space-y-2 text-sm">
                <div className="text-sm text-gray-700">
                  {t('import.added', { count: result.imported })}
                </div>
                <div className="flex justify-between">
                  <span>{t('import.imported')}</span>
                  <span className="font-medium text-green-600">{result.imported}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('import.skipped')}</span>
                  <span className="font-medium text-yellow-600">{result.skipped}</span>
                </div>
                {result.outcomes.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-2 font-medium text-gray-900">Import outcomes</div>
                    <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2">
                      {result.outcomes.map((item, index) => (
                        <div key={`${item.full_name}-${index}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className="truncate text-gray-700">{item.full_name}</span>
                          <span className={item.outcome === 'created' ? 'font-medium text-green-700' : item.outcome === 'already_a_guest' ? 'font-medium text-amber-700' : 'font-medium text-blue-700'}>
                            {item.outcome === 'created' ? 'Created' : item.outcome === 'already_a_guest' ? 'Already a guest' : 'Matched existing contact'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-4">
                    <div className="text-red-600 font-medium mb-2">{t('import.errorsTitle')}</div>
                    <div className="bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                      {result.errors.map((error, index) => (
                        <div key={index} className="text-sm text-red-700">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {result.success && (
                <div className="text-sm text-gray-600">
                  {t('import.autoClose')}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* File Upload */}
              <div className="space-y-2">
                <Label htmlFor="csv-file">{t('import.csvFile')}</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                {file && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="h-4 w-4" />
                    <span>{file.name}</span>
                  </div>
                )}
                {headers.length > 0 && (
                  <div className="space-y-1" aria-live="polite">
                    <span className="text-xs font-medium text-gray-700">Detected headers</span>
                    <div className="flex flex-wrap gap-1">
                      {headers.map((header) => (
                        <span key={header} className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">{header}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Column Mapping */}
              <div className="space-y-4">
                <Label className="text-base font-medium">{t('import.columnMapping')}</Label>
                <div className="text-sm text-gray-600">
                  {t('import.columnMappingHint')}
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="full-name-mapping">
                      {t('import.fields.fullName')} <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="full-name-mapping"
                      value={mapping.full_name}
                      onChange={(event) => handleMappingChange('full_name', event.target.value)}
                      disabled={headers.length === 0}
                      required
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a CSV header</option>
                      {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                    </select>
                    <div className="text-xs text-gray-500 mt-1">
                      Required mapping
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email-mapping">
                      {t('import.fields.email')}
                    </Label>
                    <select
                      id="email-mapping"
                      value={mapping.email}
                      onChange={(event) => handleMappingChange('email', event.target.value)}
                      disabled={headers.length === 0}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a CSV header</option>
                      {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                    </select>
                    <div className="text-xs text-gray-500 mt-1">
                      Map email or phone. Rows require a full name and at least one contact method.
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="phone-mapping">{t('import.fields.phone')}</Label>
                    <select
                      id="phone-mapping"
                      value={mapping.phone}
                      onChange={(event) => handleMappingChange('phone', event.target.value)}
                      disabled={headers.length === 0}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Not mapped</option>
                      {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="relationship-mapping">{t('import.fields.relationship')}</Label>
                    <select
                      id="relationship-mapping"
                      value={mapping.relationship}
                      onChange={(event) => handleMappingChange('relationship', event.target.value)}
                      disabled={headers.length === 0}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Not mapped</option>
                      {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleClose}>
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" disabled={loading || !file} className="flex items-center gap-2">
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      {t('import.importButton')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
